import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import HashtagInput from '@/components/HashtagInput';
import { UseFormReturn } from 'react-hook-form';
import { PostFormValues } from '@/hooks/post/usePostForm';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { IonIcon } from '@ionic/react';
import { earth } from 'ionicons/icons';
import { Users, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUserProfile } from '@/context/UserProfileContext';

type PostFormFieldsProps = {
  form: UseFormReturn<PostFormValues>;
  onCaptionChange: (value: string) => void;
  onHashtagSelect: (hashtag: string) => void;
  onAudienceClick: () => void;
};

export function PostFormFields({
  form,
  onCaptionChange,
  onHashtagSelect,
  onAudienceClick
}: PostFormFieldsProps) {
  const { t } = useTranslation('MGP');
  const { profile: userProfile } = useUserProfile();

  if (!userProfile) {
    return null;
  }

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-2">
        <GolfAvatar size={48} imageUrl={userProfile.imageUrl} />
        <div className="flex flex-col">
          <span className="text-base font-semibold">
            {userProfile.username}
          </span>
          <div
            className="flex items-center gap-1 text-xs rounded-full bg-blue-500/30 px-2 py-1 w-fit cursor-pointer"
            onClick={onAudienceClick}
          >
            {form.getValues('audience') === 0 && (
              <>
                <IonIcon icon={earth} className="size-4 text-blue-500" />
                <span>{t('public')}</span>
              </>
            )}
            {form.getValues('audience') === 1 && (
              <>
                <Users className="size-3 text-blue-500" />
                <span>{t('friends')}</span>
              </>
            )}
            {form.getValues('audience') === 2 && (
              <>
                <Lock className="size-3 text-blue-500" />
                <span>{t('private')}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <FormField
        control={form.control}
        name="caption"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <HashtagInput
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  onCaptionChange(value);
                }}
                onHashtagSelect={onHashtagSelect}
              />
            </FormControl>
            <div className="text-right text-sm text-gray-500">
              {field.value.length}
              {' '}
              / 1024
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
